import "react-data-grid/lib/styles.css";
import DataGrid from "react-data-grid";
import { useEffect, useMemo, useState } from "react";
import { Icon } from "../../tremor/Icon";
import { Title } from "../../tremor/Text";
import { FunnelIcon as FunnelIconOutline } from "@heroicons/react/24/outline";
import { FunnelIcon as FunnelIconSolid } from "@heroicons/react/24/solid";
import { BarsArrowDownIcon, BarsArrowUpIcon } from "@heroicons/react/20/solid";
import React from "react";
import { MultiSelect } from "../../tremor/MultiSelect";
import { useTheme } from "../../layouts/Dashboard/useTheme";
import { useBackend } from "../../layouts/Wrapper";
import { Button } from "../../tremor/Button";
import { Input } from "../../tremor/Input";
import { Text } from "../../tremor/Text";
function urlify(text) {
    // Regex to detect markdown images: ![alt text](url)
    const markdownImageRegex = /!\[([^\]]*)\]\((https?:\/\/[^\s)]+)\)/g;
    // Regex to detect markdown links: [text](url)
    const markdownLinkRegex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
    // Regex to detect standalone URLs: https://example.com
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    let text1 = text + "";
    // First, replace markdown images
    text1 = text1.replace(markdownImageRegex, function (_, altText, url) {
        let properUrl = url.startsWith("http") ? url : "https://" + url;
        return `<img style="height:28px;width:28px;object-fit:contain;object-position:50% 50%;" src="${properUrl}" alt="${altText}" />`;
    });
    // Next, replace markdown links
    text1 = text1.replace(markdownLinkRegex, function (_, linkText, url) {
        let properUrl = url.startsWith("http") ? url : "https://" + url;
        return `<a target="_blank" href="${properUrl}">${linkText}</a>`;
    });
    if (text1 !== text) {
        return text1;
    }
    // Finally, replace standalone URLs (avoiding already replaced markdown links and images)
    return text1.replace(urlRegex, function (url) {
        let properUrl = url.startsWith("http") ? url : "https://" + url;
        return `<a target="_blank" href="${properUrl}">${url}</a>`;
    });
}
const cellRenderer = (obj) => {
    let content = urlify(obj.row[obj.column.key]);
    return React.createElement("span", { dangerouslySetInnerHTML: { __html: content } });
};
export const TableChart = ({ data }) => {
    const [sorting, setSorting] = useState([]);
    const [filterEnabled, setFilterEnabled] = useState(false);
    const [options, setOptions] = useState({});
    const [filters, setFilters] = useState({});
    const theme = useTheme();
    const { containerRef } = useBackend();
    let [objects, fields] = useMemo(() => {
        var _a, _b, _c, _d;
        let newRows = [];
        let label = ((_d = (_c = (_b = (_a = data.options) === null || _a === void 0 ? void 0 : _a.scales) === null || _b === void 0 ? void 0 : _b.x) === null || _c === void 0 ? void 0 : _c.title) === null || _d === void 0 ? void 0 : _d.text) || "Label";
        data.data.datasets[0].data.map((item, i) => {
            let row = {};
            if (data.data.labels && data.data.labels[i]) {
                row = {
                    [label]: data.data.labels[i],
                };
            }
            data.data.datasets.map((a, j) => {
                row[a.label] = a.data[i];
            });
            newRows.push(row);
        });
        if (data.data.labels) {
            let newFields = [
                label,
                ...data.data.datasets.map((a) => a.label),
            ];
            return [newRows, newFields];
        }
        else {
            let newFields = data.data.datasets.map((a) => a.label);
            return [newRows, newFields];
        }
    }, [data]);
    useEffect(() => {
        let newFilters = {};
        let newOptions = {};
        fields.forEach((field) => {
            newFilters[field] = [];
            newOptions[field] = [];
            objects.forEach((row) => {
                if (!newOptions[field].includes(row[field])) {
                    newOptions[field].push(row[field]);
                }
            });
        });
        setOptions(newOptions);
        setFilters(newFilters);
    }, [objects, fields]);
    let rows = useMemo(() => {
        let filtered = [...objects];
        Object.keys(filters).forEach((key) => {
            if (filters[key].length > 0) {
                filtered = filtered.filter((a) => (filters[key] + "").includes(a[key] + ""));
            }
        });
        if (sorting.length === 0) {
            return filtered;
        }
        const { columnKey, direction } = sorting[0];
        return filtered.sort((a, b) => {
            let reverse = direction === "ASC" ? 1 : -1;
            if (typeof a[columnKey] === "number") {
                return (a[columnKey] - b[columnKey]) * reverse;
            }
            return (a[columnKey].localeCompare(b[columnKey]) * reverse);
        });
    }, [objects, sorting, filters]);
    let columns = useMemo(() => {
        return Object.keys(options).map((a) => ({
            key: a,
            name: a,
            renderCell: cellRenderer,
            renderHeaderCell: (p) => {
                const isMatchingFilterColumn = sorting.length > 0 && sorting[0].columnKey === p.column.name;
                const filterTypeClass = isMatchingFilterColumn
                    ? "onvo-table-widget-filter-active"
                    : "onvo-table-widget-filter-inactive";
                return (React.createElement("div", { className: "onvo-table-widget-header onvo-flex onvo-h-full onvo-flex-col onvo-justify-center onvo-gap-2", style: { lineHeight: "12px" } },
                    React.createElement("div", { className: "onvo-table-widget-header-title onvo-flex onvo-flex-row onvo-items-center onvo-justify-between" },
                        p.column.name,
                        React.createElement("div", { className: `${filterTypeClass} onvo-flex onvo-flex-row` },
                            isMatchingFilterColumn && (React.createElement(Icon, { icon: sorting[0].direction === "ASC"
                                    ? BarsArrowUpIcon
                                    : BarsArrowDownIcon, size: "sm" })),
                            React.createElement(Icon, { onClick: (e) => {
                                    setFilterEnabled((a) => !a);
                                    e.preventDefault();
                                    e.stopPropagation();
                                }, icon: filterEnabled ? FunnelIconSolid : FunnelIconOutline, size: "sm" }))),
                    filterEnabled && (React.createElement(MultiSelect, { container: containerRef === null || containerRef === void 0 ? void 0 : containerRef.current, placeholder: "Filter", className: "-onvo-mt-2", onValueChange: (val) => {
                            setFilters(Object.assign(Object.assign({}, filters), { [p.column.key]: val }));
                        }, items: options[a].map((c) => ({
                            label: c,
                            value: c,
                        })) }))));
            },
        }));
    }, [options, filterEnabled, sorting]);
    return (React.createElement("div", { className: "onvo-table-widget onvo-h-full onvo-flex onvo-flex-col onvo-relative" },
        React.createElement(Title, { className: "onvo-table-widget-title onvo-text-md !onvo-text-[#111] dark:!onvo-text-[#ddd] onvo-font-override onvo-my-0" }, data.options.plugins.title.text),
        React.createElement(DataGrid, { className: "onvo-table-widget-data-grid onvo-fill-grid onvo-mt-3 onvo-h-full onvo-rounded-t-xl onvo-border-solid onvo-border onvo-border-gray-200 dark:onvo-border-gray-800 " +
                (theme === "dark" ? "rdg-dark" : "rdg-light"), defaultColumnOptions: {
                sortable: true,
                resizable: true,
            }, enableVirtualization: true, headerRowHeight: filterEnabled ? 80 : 35, bottomSummaryRows: [
                React.createElement("div", { className: "onvo-flex onvo-flex-row onvo-items-center onvo-justify-between" }, "Test")
            ], onSortColumnsChange: setSorting, sortColumns: sorting, columns: columns, rows: rows }),
        false && (React.createElement(Text, { className: "onvo-flex onvo-flex-row onvo-text-sm onvo-items-center onvo-gap-2 onvo-mt-0 onvo-w-full onvo-rounded-b-lg onvo-background-color onvo-py-1 onvo-px-2 onvo-border-solid onvo-border onvo-border-gray-200 dark:onvo-border-gray-800" },
            "Display ",
            React.createElement(Input, { className: "onvo-w-12 onvo-max-w-12", placeholder: "100", inputClassName: "!onvo-py-1" }),
            " rows in page ",
            React.createElement(Input, { className: "onvo-w-12 onvo-max-w-12", placeholder: "1", inputClassName: "!onvo-py-1" }),
            " of 100",
            React.createElement("div", { className: "onvo-flex-grow" }),
            React.createElement(Button, { variant: "secondary", className: "!onvo-py-1" }, "Previous"),
            " ",
            React.createElement(Button, { variant: "secondary", className: "!onvo-py-1" }, "Next")))));
};
//# sourceMappingURL=TableChart.js.map